/**
 * Integrytas Group - Base App (MOBILE)
 * "Submit new user" form for CSM.USER.PROVISION (mock C08).
 *
 * Touch-first counterpart of the desktop SubmitNewUserForm
 * (src/pages/users/UserProvisionPage.tsx). A DISTINCT presentation — grouped
 * field cards + a sticky submit bar, native selects and chip multiselect — over
 * the SAME data/logic layer (§16). It is PRESENTATION ONLY: no new backend.
 *   - reference data via the shared hooks/queries the desktop uses (jobs with
 *     their approver-chain count, active roles, active users for the manager
 *     picker, org lookups, language options, job-default roles);
 *   - submission calls csm_provision_submit — the module's sanctioned
 *     RPC, identical params to the desktop form — which writes the request, its
 *     requested roles and queues the first-approver email through the Common
 *     Routine (§2). Forensic stamping is applied server-side from the JWT.
 */

/* rbac-affordance-ok: raising a provisioning REQUEST for someone else to sign
   off. csm_provision_submit accepts any authenticated caller by design
   and creates no user; the privileged half is the signoff, which is gated
   per-request server-side. */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus, Check } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useOrgLookups } from '../../lib/use_org_lookups';
import { useLanguageOptions } from '../../lib/use_language_options';
import { fetchAllJobDefaultRoles } from '../../lib/job_default_roles';
import type { AuthSession } from '../../types/auth';

interface TlRow { locale_code: string; display_text: string }
interface JobOpt { job_description_id: number; display_text: string; approver_count: number }
interface RoleOpt { role_id: number; internal_code: string; display_text: string }
interface UserOpt { user_id: number; display_name: string; email: string }

interface MobileSubmitNewUserFormProps {
  session: AuthSession;
  /** Called after a successful submit (parent switches to "My submissions" + reloads). */
  onSubmitted: () => void;
}

/** Pick the active-locale label from a _tl set, falling back to EN_US then a code. */
function pickTl(tls: TlRow[] | undefined, locale: string, fallback: string): string {
  const list = tls ?? [];
  const exact = list.find((tl) => tl.locale_code === locale);
  const fb = list.find((tl) => tl.locale_code === 'EN_US') ?? list[0];
  return exact?.display_text ?? fb?.display_text ?? fallback;
}

export function MobileSubmitNewUserForm({ session, onSubmitted }: MobileSubmitNewUserFormProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const orgLookups = useOrgLookups(session.effective_user.preferred_locale);
  const languages = useLanguageOptions();

  // ---- form fields -------------------------------------------------------
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [locale, setLocale] = useState(session.effective_user.preferred_locale);
  const [jobId, setJobId] = useState<number | ''>('');
  const [managerId, setManagerId] = useState<number | null>(null);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [divisionId, setDivisionId] = useState<number | null>(null);
  const [businessUnitId, setBusinessUnitId] = useState<number | null>(null);
  const [officeId, setOfficeId] = useState<number | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<Set<number>>(new Set());
  const [reason, setReason] = useState('');

  // ---- reference data ----------------------------------------------------
  const [jobs, setJobs] = useState<JobOpt[]>([]);
  const [roles, setRoles] = useState<RoleOpt[]>([]);
  const [users, setUsers] = useState<UserOpt[]>([]);
  const [jobDefaultRoles, setJobDefaultRoles] = useState<Map<number, number[]>>(new Map());

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Jobs (with active approver-chain count) + roles, following the active locale.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data: jData } = await apiDb
        .from('csm_job_description')
        .select(`
          job_description_id, internal_code, is_active, approval_hierarchy_id,
          csm_job_description_tl ( locale_code, display_text ),
          csm_approval_hierarchy:approval_hierarchy_id (
            csm_approval_hierarchy_step ( approval_hierarchy_step_id, is_active )
          )
        `)
        .eq('is_active', 'Y')
        .order('internal_code');
      if (cancelled) return;
      type JobDataRow = {
        job_description_id: number;
        internal_code: string;
        csm_job_description_tl?: TlRow[];
        csm_approval_hierarchy?: { csm_approval_hierarchy_step?: { approval_hierarchy_step_id: number; is_active: string }[] } | null;
      };
      setJobs(((jData ?? []) as JobDataRow[]).map((j) => ({
        job_description_id: j.job_description_id,
        display_text: pickTl(j.csm_job_description_tl, activeLocale, j.internal_code),
        approver_count: (j.csm_approval_hierarchy?.csm_approval_hierarchy_step ?? []).filter((s) => s.is_active === 'Y').length,
      })));

      const { data: rData } = await apiDb
        .from('csm_role_master')
        .select('role_id, internal_code, is_active, csm_role_master_tl ( locale_code, display_text )')
        .eq('is_active', 'Y')
        .order('internal_code');
      if (cancelled) return;
      type RoleDataRow = { role_id: number; internal_code: string; csm_role_master_tl?: TlRow[] };
      setRoles(((rData ?? []) as RoleDataRow[]).map((r) => ({
        role_id: r.role_id,
        internal_code: r.internal_code,
        display_text: pickTl(r.csm_role_master_tl, activeLocale, r.internal_code),
      })));
    })();
    return () => { cancelled = true; };
  }, [activeLocale]);

  // Active users for the manager picker + the job → default-roles map.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await apiDb
        .from('csm_user_master')
        .select('user_id, display_name, email')
        .eq('is_active', 'Y')
        .order('display_name');
      if (!cancelled) setUsers((data ?? []) as UserOpt[]);
      const map = await fetchAllJobDefaultRoles();
      if (!cancelled) setJobDefaultRoles(map);
    })();
    return () => { cancelled = true; };
  }, []);

  // Pre-select org defaults once the lookups have loaded (user can still change them).
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  useEffect(() => {
    if (orgLookups.loading || defaultsApplied) return;
    setCompanyId((prev) => prev ?? orgLookups.defaults.company_id);
    setDivisionId((prev) => prev ?? orgLookups.defaults.division_id);
    setBusinessUnitId((prev) => prev ?? orgLookups.defaults.business_unit_id);
    setOfficeId((prev) => prev ?? orgLookups.defaults.office_id);
    setDefaultsApplied(true);
  }, [orgLookups.loading, orgLookups.defaults, defaultsApplied]);

  const roleLabels = useMemo(() => new Map(roles.map((r) => [r.role_id, r.display_text])), [roles]);
  const job = jobs.find((j) => j.job_description_id === jobId);

  // On job change, add that job's (active) default roles to the selection — never
  // removing a manual pick (touch-first: predictable, no reconciliation modal).
  const handleJobChange = (value: string) => {
    const id = value === '' ? '' : Number(value);
    setJobId(id);
    if (id === '') return;
    const defaults = (jobDefaultRoles.get(id) ?? []).filter((rid) => roleLabels.has(rid));
    if (defaults.length) setSelectedRoles((prev) => new Set([...prev, ...defaults]));
  };

  const toggleRole = (roleId: number) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId); else next.add(roleId);
      return next;
    });
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!email || !email.includes('@')) throw new Error(t('mobile.provision.submit.err_email', 'A valid email is required.'));
      if (!displayName.trim()) throw new Error(t('mobile.provision.submit.err_name', 'Display name is required.'));
      if (!jobId) throw new Error(t('mobile.provision.submit.err_job', 'Pick a job description.'));
      if (selectedRoles.size === 0) throw new Error(t('mobile.provision.submit.err_roles', 'Select at least one role.'));
      if (!reason.trim() || reason.trim().length < 5) throw new Error(t('mobile.provision.submit.err_reason', 'Provide a business reason of at least 5 characters.'));
      if (job && job.approver_count < 1) {
        throw new Error(t('mobile.provision.submit.err_no_approver', 'The chosen job has no approver chain. Configure approvers in Job Descriptions first.'));
      }

      // Same APR-backed routine the desktop screen calls (§16: one data layer,
      // two presentation layers). Roles go as internal_codes — apr_request_line
      // keys on a stable business code, not an id.
      const roleCodes = roles.filter((r) => selectedRoles.has(r.role_id)).map((r) => r.internal_code);
      const { data: res, error: e } = await apiDb.rpc<{ ok: boolean; error_code?: string }>(
        'csm_provision_submit', {
          p_email:              email.trim(),
          p_display_name:       displayName.trim(),
          p_job_description_id: jobId,
          p_role_codes:         roleCodes,
          p_preferred_locale:   locale,
          p_manager_user_id:    managerId,
          p_business_reason:    reason.trim(),
          p_company_id:         companyId,
          p_division_id:        divisionId,
          p_business_unit_id:   businessUnitId,
          p_office_id:          officeId,
        });
      if (e) throw e;
      // The routine reports refusals in its envelope rather than raising.
      if (!res?.ok) {
        throw new Error(t(`user_provision.error.${(res?.error_code ?? '').replace(/\./g, '_')}`,
                          t('mobile.provision.submit.err_failed', 'Submit failed.')));
      }
      onSubmitted();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const orgSelect = (
    labelKey: string, fallback: string, value: number | null,
    options: { id: number; display_text: string }[], onChange: (v: number | null) => void,
  ) => (
    <div className="m-formrow">
      <label>{t(labelKey, fallback)}</label>
      <select className="m-sel" value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}>
        <option value="">{t('common.none', 'None')}</option>
        {options.map((o) => <option key={o.id} value={o.id}>{o.display_text}</option>)}
      </select>
    </div>
  );

  return (
    <section className="m-screen">
      {/* Identity */}
      <div className="m-fieldcard">
        <div className="m-formrow">
          <label htmlFor="m-np-name">{t('mobile.provision.submit.name', 'Full name')}</label>
          <input id="m-np-name" className="m-inp" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t('mobile.provision.submit.name_ph', 'e.g. Jordan Rivera')} />
        </div>
        <div className="m-formrow">
          <label htmlFor="m-np-email">{t('mobile.provision.submit.email', 'Email')}</label>
          <input id="m-np-email" className="m-inp" type="email" inputMode="email" autoCapitalize="none"
            value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" />
        </div>
        <div className="m-formrow">
          <label htmlFor="m-np-locale">{t('mobile.provision.submit.locale', 'Preferred language')}</label>
          <select id="m-np-locale" className="m-sel" value={locale} onChange={(e) => setLocale(e.target.value)}>
            {languages.options.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
          </select>
        </div>
      </div>

      {/* Assignment */}
      <div className="m-fieldcard">
        <div className="m-formrow">
          <label htmlFor="m-np-job">{t('mobile.provision.submit.job', 'Job description')}</label>
          <select id="m-np-job" className="m-sel" value={jobId} onChange={(e) => handleJobChange(e.target.value)}>
            <option value="">{t('mobile.provision.submit.job_ph', 'Select a job…')}</option>
            {jobs.map((j) => <option key={j.job_description_id} value={j.job_description_id}>{j.display_text}</option>)}
          </select>
          {job && job.approver_count < 1 && (
            <div className="m-formrow-hint" style={{ color: 'var(--color-danger)' }}>
              {t('mobile.provision.submit.no_approver_hint', 'This job has no approver chain — it cannot be submitted yet.')}
            </div>
          )}
        </div>
        <div className="m-formrow">
          <label htmlFor="m-np-mgr">{t('mobile.provision.submit.manager', 'Manager (optional)')}</label>
          <select id="m-np-mgr" className="m-sel" value={managerId ?? ''} onChange={(e) => setManagerId(e.target.value === '' ? null : Number(e.target.value))}>
            <option value="">{t('mobile.provision.submit.manager_none', '(no manager)')}</option>
            {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.display_name || u.email}</option>)}
          </select>
        </div>
        {orgSelect('mobile.provision.submit.company', 'Company', companyId, orgLookups.companies, setCompanyId)}
        {orgSelect('mobile.provision.submit.division', 'Division', divisionId, orgLookups.divisions, setDivisionId)}
        {orgSelect('mobile.provision.submit.business_unit', 'Business unit', businessUnitId, orgLookups.business_units, setBusinessUnitId)}
        {orgSelect('mobile.provision.submit.office', 'Office', officeId, orgLookups.offices, setOfficeId)}
      </div>

      {/* Roles */}
      <div className="m-fieldcard">
        <div className="m-formrow">
          <label>{t('mobile.provision.submit.roles', 'Requested roles')}</label>
          {roles.length === 0 ? (
            <div className="m-formrow-hint">{t('mobile.provision.submit.no_roles', 'No active roles available.')}</div>
          ) : (
            <div className="m-chips" style={{ margin: '2px 0 0' }}>
              {roles.map((r) => (
                <button key={r.role_id} type="button"
                  className={'m-chip' + (selectedRoles.has(r.role_id) ? ' m-active' : '')}
                  onClick={() => toggleRole(r.role_id)}>
                  {r.display_text}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Business reason */}
      <div className="m-fieldcard">
        <div className="m-formrow">
          <label htmlFor="m-np-reason">{t('mobile.provision.submit.reason', 'Business reason')}</label>
          <textarea id="m-np-reason" className="m-inp" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder={t('mobile.provision.submit.reason_ph', 'Why does this person need access?')} />
        </div>
      </div>

      {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

      {/* Sticky submit bar (C08) */}
      <div className="m-submitbar">
        <button type="button" className="m-btn m-btn-primary" style={{ width: '100%' }} disabled={busy} onClick={() => void submit()}>
          {busy ? <>{t('common.saving', 'Saving…')}</> : <><UserPlus size={16} /><Check size={16} />{t('mobile.provision.submit.action', 'Submit request')}</>}
        </button>
      </div>
    </section>
  );
}
